import { gsap } from 'gsap'
import { getSceneImagePath } from '../../../utils/pathResolver'
import { SaveStateManager } from '../../../features/save-state'
import { getAudioManager } from '../../../features/audio'
import {
  SCENE_ID,
  BUBBLE_DISPLAY_MS,
  WRONG_BUBBLE_COUNT,
  STAGES,
  type AsociaceStage,
  type AsociaceItem,
} from './constants'
import { showGlobalScrollIndicator, hideGlobalScrollIndicator } from '../../../utils/scroll-indicator'
import type { SceneData } from '../../scenes/types'
import './Asociace.css'

interface AsociaceState {
  found: Record<string, string[]>
  unlockedStage: number
  completed: boolean
}

function loadState(): AsociaceState {
  const saved = SaveStateManager.load()
  const mg = saved?.minigames?.[SCENE_ID] as Partial<AsociaceState> | undefined
  if (mg) {
    return {
      found: mg.found ?? {},
      unlockedStage: mg.unlockedStage ?? 0,
      completed: mg.completed ?? false,
    }
  }
  return { found: {}, unlockedStage: 0, completed: false }
}

function saveState(state: AsociaceState): void {
  const saved = SaveStateManager.load() ?? SaveStateManager.createInitialState()
  if (!saved.minigames) saved.minigames = {}
  saved.minigames[SCENE_ID] = {
    found: { ...state.found },
    unlockedStage: state.unlockedStage,
    completed: state.completed,
  }
  SaveStateManager.saveImmediate(saved)
}

function isStageFullyFound(stageDef: AsociaceStage, found: Record<string, string[]>): boolean {
  const correctIds = stageDef.items.filter((i) => i.correct).map((i) => i.id)
  if (correctIds.length === 0) return false
  const foundSet = new Set(found[stageDef.id] ?? [])
  return correctIds.every((id) => foundSet.has(id))
}

/**
 * Index of the first stage that still has missing correct picks, or STAGES.length if all are done.
 */
function firstIncompleteStageIndex(found: Record<string, string[]>): number {
  for (let i = 0; i < STAGES.length; i++) {
    if (!isStageFullyFound(STAGES[i], found)) return i
  }
  return STAGES.length
}

/**
 * Minimum unlockedStage implied by saved `found` (which is written before the delayed onStageComplete).
 * Keeps stage wrappers visible after leaving mid-timeout or if unlockedStage was never persisted.
 */
function unlockedStageImpliedByFound(found: Record<string, string[]>): number {
  const incomplete = firstIncompleteStageIndex(found)
  if (incomplete >= STAGES.length) return STAGES.length - 1
  return incomplete
}

function reconcileUnlockedStage(state: AsociaceState): void {
  const implied = unlockedStageImpliedByFound(state.found)
  const merged = Math.min(Math.max(state.unlockedStage, implied), STAGES.length - 1)
  if (merged !== state.unlockedStage) {
    state.unlockedStage = merged
    saveState(state)
  }
}

function reconcileCompletedFlag(state: AsociaceState): void {
  const allDone = firstIncompleteStageIndex(state.found) >= STAGES.length
  if (allDone && !state.completed) {
    state.completed = true
    saveState(state)
  }
}

function isElementInViewport(el: HTMLElement): boolean {
  const rect = el.getBoundingClientRect()
  return rect.top < window.innerHeight && rect.bottom > 0
}

/** Poll for the next .scene-wrapper sibling after the minigame's own wrapper. */
function findNextSceneWrapper(root: HTMLElement, onFound: (el: HTMLElement) => void): void {
  const MAX_ATTEMPTS = 20
  const INTERVAL_MS = 100
  let attempts = 0

  function check() {
    attempts++
    const sceneWrapper = root.closest('.scene-wrapper')
    if (sceneWrapper) {
      let next = sceneWrapper.nextElementSibling as HTMLElement | null
      while (next && !next.classList.contains('scene-wrapper')) {
        next = next.nextElementSibling as HTMLElement | null
      }
      if (next) {
        onFound(next)
        return
      }
    }
    if (attempts < MAX_ATTEMPTS) {
      setTimeout(check, INTERVAL_MS)
    }
  }

  check()
}

export function createMinigame(container: HTMLElement, onComplete?: () => void, _sceneData?: SceneData): HTMLElement {
  const root = document.createElement('div')
  root.className = 'minigame-asociace'
  root.setAttribute('data-minigame', SCENE_ID)

  const state = loadState()
  reconcileUnlockedStage(state)
  reconcileCompletedFlag(state)
  let wrongBubbleIndex = 0

  // Track what the scroll indicator is waiting for
  let scrollTarget: HTMLElement | null = null

  const stageWrappers: HTMLElement[] = []
  const stageUnlockCallbacks: Array<() => void> = []

  STAGES.forEach((stageDef, stageIdx) => {
    const { wrapper, unlock } = createStage(
      stageDef,
      stageIdx,
      state,
      () => onStageComplete(stageIdx),
      () => wrongBubbleIndex++ % WRONG_BUBBLE_COUNT,
    )
    stageWrappers.push(wrapper)
    stageUnlockCallbacks.push(unlock)
    root.appendChild(wrapper)
  })

  for (let i = 0; i <= state.unlockedStage && i < STAGES.length; i++) {
    stageUnlockCallbacks[i]()
  }

  if (state.completed) {
    if (onComplete) onComplete()
  }

  function onStageComplete(stageIdx: number) {
    const nextIdx = stageIdx + 1
    if (nextIdx < STAGES.length) {
      state.unlockedStage = Math.max(state.unlockedStage, nextIdx)
      saveState(state)
      stageUnlockCallbacks[nextIdx]()
      // Show scroll indicator until next stage is in viewport
      scrollTarget = stageWrappers[nextIdx]
      showGlobalScrollIndicator()
    } else {
      state.completed = true
      saveState(state)
      if (onComplete) onComplete()
      // Show scroll indicator until the next scene is visible.
      // The story engine defers scene appending (rAF + setTimeout), so poll until the next
      // scene-wrapper appears in the DOM.
      showGlobalScrollIndicator()
      findNextSceneWrapper(root, (el) => {
        scrollTarget = el
      })
    }
  }

  function onScroll() {
    if (!scrollTarget) return
    if (isElementInViewport(scrollTarget)) {
      hideGlobalScrollIndicator()
      scrollTarget = null
    }
  }

  window.addEventListener('scroll', onScroll, { passive: true })
  window.addEventListener('resize', onScroll, { passive: true })
  ;(root as unknown as Record<string, () => void>).__minigameCleanup = () => {
    window.removeEventListener('scroll', onScroll)
    window.removeEventListener('resize', onScroll)
    hideGlobalScrollIndicator()
  }

  return root
}

function createStage(
  stageDef: AsociaceStage,
  _stageIdx: number,
  state: AsociaceState,
  onComplete: () => void,
  getNextWrongIdx: () => number,
): { wrapper: HTMLElement; unlock: () => void } {
  const wrapperEl = document.createElement('div')
  wrapperEl.className = 'minigame-asociace__stage-wrapper'
  wrapperEl.style.display = 'none'

  const edgeTop = document.createElement('div')
  edgeTop.className = 'minigame-asociace__edge minigame-asociace__edge--top'
  const edgeTopImg = document.createElement('img')
  edgeTopImg.src = getSceneImagePath(SCENE_ID, 'bg-edge.png')
  edgeTopImg.alt = ''
  edgeTop.appendChild(edgeTopImg)
  wrapperEl.appendChild(edgeTop)

  const stageEl = document.createElement('div')
  stageEl.className = 'minigame-asociace__stage'
  stageEl.setAttribute('data-stage', stageDef.id)
  const bgDiv = document.createElement('div')
  bgDiv.className = 'minigame-asociace__stage-bg'
  bgDiv.style.backgroundColor = stageDef.backgroundColor
  stageEl.appendChild(bgDiv)

  const correctCount = stageDef.items.filter((i) => i.correct).length
  const counterWrap = document.createElement('div')
  counterWrap.className = 'minigame-asociace__counter-wrap'
  const counterEl = document.createElement('div')
  counterEl.className = 'minigame-asociace__counter'
  counterEl.setAttribute('aria-live', 'polite')
  counterWrap.appendChild(counterEl)
  stageEl.appendChild(counterWrap)

  const itemsContainer = document.createElement('div')
  itemsContainer.className = 'minigame-asociace__items'
  itemsContainer.style.aspectRatio = String(stageDef.aspectRatio)
  stageEl.appendChild(itemsContainer)

  wrapperEl.appendChild(stageEl)

  const edgeBottom = document.createElement('div')
  edgeBottom.className = 'minigame-asociace__edge minigame-asociace__edge--bottom'
  const edgeBottomImg = document.createElement('img')
  edgeBottomImg.src = getSceneImagePath(SCENE_ID, 'bg-edge.png')
  edgeBottomImg.alt = ''
  edgeBottom.appendChild(edgeBottomImg)
  wrapperEl.appendChild(edgeBottom)

  const correctIds = new Set(stageDef.items.filter((i) => i.correct).map((i) => i.id))
  const foundInStage = new Set<string>(state.found[stageDef.id] ?? [])
  const stageCompleted = foundInStage.size >= correctIds.size && correctIds.size > 0
  let locked = stageCompleted

  function updateCounter() {
    counterEl.textContent = `${foundInStage.size} / ${correctCount}`
  }
  updateCounter()

  let activeBubbleTimeout: ReturnType<typeof setTimeout> | null = null
  let activeBubbleEl: HTMLElement | null = null

  function showBubbleAtItem(
    btn: HTMLElement,
    itemDef: AsociaceItem,
    src: string,
    fallbackMs: number,
    audioFile?: string,
  ) {
    if (activeBubbleTimeout) {
      clearTimeout(activeBubbleTimeout)
      activeBubbleTimeout = null
    }
    if (activeBubbleEl && activeBubbleEl.parentNode) {
      activeBubbleEl.parentNode.removeChild(activeBubbleEl)
    }

    const btnRect = btn.getBoundingClientRect()
    const btnCenterY = btnRect.top + btnRect.height / 2
    const pos: 'top' | 'bottom' = btnCenterY < window.innerHeight / 2 ? 'bottom' : 'top'

    const bubbleWidth = itemDef.bubbleWidth ?? itemDef.width
    const bubble = document.createElement('div')
    bubble.className = 'minigame-asociace__bubble'
    bubble.style.width = `${bubbleWidth}%`

    const itemCenterX = itemDef.position.x + itemDef.width / 2
    const clampedLeft = Math.max(0, Math.min(itemCenterX - bubbleWidth / 2, 100 - bubbleWidth))
    bubble.style.left = `${clampedLeft}%`

    const img = document.createElement('img')
    img.src = src
    img.alt = ''
    bubble.appendChild(img)

    const containerRect = itemsContainer.getBoundingClientRect()
    const anchorTopPct = ((btnRect.top - containerRect.top) / containerRect.height) * 100
    const anchorBottomPct = ((btnRect.bottom - containerRect.top) / containerRect.height) * 100
    bubble.style.top = `${pos === 'bottom' ? anchorBottomPct : anchorTopPct}%`

    if (pos === 'bottom') {
      gsap.set(bubble, { yPercent: 0, scale: 0, transformOrigin: 'center top' })
    } else {
      gsap.set(bubble, { yPercent: -100, scale: 0, transformOrigin: 'center bottom' })
    }

    itemsContainer.appendChild(bubble)
    gsap.to(bubble, { scale: 1, duration: 0.35, ease: 'back.out(1.7)' })

    activeBubbleEl = bubble

    const scheduleDismiss = (ms: number) => {
      activeBubbleTimeout = setTimeout(() => {
        gsap.to(bubble, {
          scale: 0,
          duration: 0.3,
          ease: 'back.in(1.7)',
          onComplete: () => {
            if (bubble.parentNode) bubble.parentNode.removeChild(bubble)
            if (activeBubbleEl === bubble) activeBubbleEl = null
          },
        })
        activeBubbleTimeout = null
      }, ms)
    }

    if (audioFile) {
      getAudioManager()
        .tryPlayCustomSound(audioFile, SCENE_ID, 'speech')
        .then((durationMs) => {
          if (activeBubbleEl !== bubble) return
          scheduleDismiss(durationMs > 0 ? durationMs + 500 : fallbackMs)
        })
    } else {
      scheduleDismiss(fallbackMs)
    }
  }

  stageDef.items.forEach((itemDef) => {
    const btn = document.createElement('button')
    btn.className = 'minigame-asociace__item-button'
    btn.type = 'button'
    btn.setAttribute('data-item-id', itemDef.id)
    btn.setAttribute('aria-label', itemDef.id)
    btn.style.left = `${itemDef.position.x}%`
    btn.style.top = `${itemDef.position.y}%`
    btn.style.width = `${itemDef.width}%`

    const itemImg = document.createElement('img')
    itemImg.className = 'minigame-asociace__item-img'
    itemImg.src = getSceneImagePath(SCENE_ID, `items/${itemDef.id}.png`)
    itemImg.alt = itemDef.id
    btn.appendChild(itemImg)

    const outlineImg = document.createElement('img')
    outlineImg.className = 'minigame-asociace__item-img-outline'
    outlineImg.src = getSceneImagePath(SCENE_ID, `items/${itemDef.id}-outline.png`)
    outlineImg.alt = ''
    btn.appendChild(outlineImg)

    if (foundInStage.has(itemDef.id)) {
      btn.classList.add('minigame-asociace__item-button--selected')
      btn.disabled = true
    }
    if (locked) {
      btn.disabled = true
    }

    btn.addEventListener('click', () => {
      if (btn.disabled || locked) return

      if (itemDef.correct) {
        btn.classList.add('minigame-asociace__item-button--selected')
        btn.disabled = true
        foundInStage.add(itemDef.id)

        if (!state.found[stageDef.id]) state.found[stageDef.id] = []
        if (!state.found[stageDef.id].includes(itemDef.id)) {
          state.found[stageDef.id].push(itemDef.id)
        }
        saveState(state)
        updateCounter()

        showBubbleAtItem(
          btn,
          itemDef,
          getSceneImagePath(SCENE_ID, `bubble-${itemDef.id}.png`),
          itemDef.bubbleDuration ?? BUBBLE_DISPLAY_MS,
          `bubble-${itemDef.id}.mp3`,
        )

        if (foundInStage.size >= correctIds.size) {
          locked = true
          itemsContainer.querySelectorAll<HTMLButtonElement>('.minigame-asociace__item-button').forEach((b) => {
            b.disabled = true
          })
          setTimeout(() => {
            onComplete()
          }, 800)
        }
      } else {
        const idx = getNextWrongIdx()
        showBubbleAtItem(
          btn,
          itemDef,
          getSceneImagePath(SCENE_ID, `bubble-wrong-${idx + 1}.png`),
          BUBBLE_DISPLAY_MS,
          `bubble-wrong-${idx + 1}.mp3`,
        )
      }
    })

    itemsContainer.appendChild(btn)
  })

  function unlock() {
    wrapperEl.style.display = ''
  }

  return { wrapper: wrapperEl, unlock }
}
